/* ==========================================================================
   BWJ Loyalty Rework — Shared State Engine
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies. One IIFE, double-init guarded. This is the
   ONLY script the 4 native loyalty modules depend on. Modules ship markup;
   this engine resolves data, computes values, decides state, and paints the
   DOM contract documented in _build/loyalty-spec.md.

   Data source priority:
     1. window.BWJLoyalty.data        (already set by the Spotler beacon)
     2. document event "bwj:loyalty"   (beacon dispatches it asynchronously)
     3. window.BWJLoyalty.bootstrap    (server-side metafield bootstrap)
     4. timeout (2000ms) -> settle to the "unknown" state

   DATA RULES (authoritative — mirror the legacy Spotler modules):
     points          = Math.round((parseFloat(balance) || 0) / 0.99)
     vouchers        = parseInt(vouchers, 10) || 0
     voucherValue    = vouchers * 2.5            (each active voucher is always €2.50)
     pointsRemaining = Math.max(25 - points, 0)
     pct             = Math.min(Math.round(points / 25 * 100), 100)
   Member gate: data is valid ONLY if status === "Active".
   LOYALTY ENGAGE (data.source === "le", UK): points arrive as whole points, vouchers = floor(points / 25) are
   vouchers the customer CAN redeem, not vouchers already issued. See computeModel.

   STATES:
     "unknown"      -> no valid data OR status !== Active (default, always renders)
     "no-voucher"   -> Active AND vouchers < 1
     "with-voucher" -> Active AND vouchers >= 1

   Defensive: never throws if elements are missing.
   ========================================================================== */
(function () {
  'use strict';

  var NS = (window.BWJLoyalty = window.BWJLoyalty || {});

  /* Double-init guard — the asset may be referenced from multiple sections. */
  if (NS.__rework_inited) return;
  NS.__rework_inited = true;

  var SETTLE_TIMEOUT_MS = 2000;
  var POINTS_PER_VOUCHER = 25;
  var EUR_PER_POINT = 0.99;
  var VOUCHER_EUR = 2.5;

  /* ---- small helpers (all null/throw safe) ----------------------------- */

  function unresolved(v) {
    /* A field is blocked/unresolved if empty or still a "{{ }}" placeholder. */
    return v == null || v === '' || String(v).indexOf('{{') === 0;
  }

  function qsa(sel, root) {
    try { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
    catch (e) { return []; }
  }

  function on(el, evt, fn, opts) {
    if (el && el.addEventListener) el.addEventListener(evt, fn, opts || false);
  }

  /* ---- currency / decimal formatting ----------------------------------- */
  /* Reads page hints from the first [data-lr-currency] / [data-lr-decimal]
     carrier (the beacon snippet emits these). Falls back to EUR + comma. */

  function readCurrencyHints() {
    var symEl = document.querySelector('[data-lr-currency]');
    var decEl = document.querySelector('[data-lr-decimal]');
    var symbol = symEl ? symEl.getAttribute('data-lr-currency') : '';
    var decimal = decEl ? decEl.getAttribute('data-lr-decimal') : '';
    if (!symbol) symbol = '€';
    if (decimal !== '.' && decimal !== ',') decimal = ',';
    return { symbol: symbol, decimal: decimal };
  }

  function formatMoney(amount, hints) {
    var n = (typeof amount === 'number' && isFinite(amount)) ? amount : 0;
    var str = n.toFixed(2);
    if (hints.decimal === ',') str = str.replace('.', ',');
    /* GBP renders symbol-first (£2.50); EUR also symbol-first (€2,50) to
       match the legacy modules. */
    return hints.symbol + str;
  }

  /* ---- i18n: read the JSON map + expose t(key, {n}) -------------------- */

  function readI18n() {
    var el = document.getElementById('lr-i18n');
    if (!el) return {};
    try { return JSON.parse(el.textContent || el.innerText || '{}') || {}; }
    catch (e) { return {}; }
  }

  var I18N = readI18n();

  function t(key, vars) {
    var s = (I18N && typeof I18N[key] === 'string') ? I18N[key] : key;
    if (vars) {
      for (var k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), String(vars[k]));
        }
      }
    }
    return s;
  }

  /* Expose t() so modules can localise their own count-dependent phrases. */
  NS.t = t;

  /* ---- data resolution ------------------------------------------------- */

  function pageProvider() {
    var el = document.getElementById('bwj-loyalty-beacon');
    return (el && el.getAttribute('data-lr-provider')) || 'spotler';
  }

  function pickData() {
    /* 1) Beacon-populated live data. On a Loyalty Engage market (UK, France) only Loyalty Engage data counts: a
       Spotler personalisation that still fires there would show the OLD Spotler balance (France had one). */
    if (NS.data && typeof NS.data === 'object') {
      if (pageProvider() !== 'le' || NS.data.source === 'le') return NS.data;
    }
    /* 3) Server bootstrap (metafields) — used only if no live data yet */
    if (NS.bootstrap && typeof NS.bootstrap === 'object') return NS.bootstrap;
    return null;
  }

  function computeModel(data) {
    /* Returns a normalised model + resolved state. Always safe. */
    var model = {
      state: 'unknown',
      status: '',
      points: 0,
      vouchers: 0,
      voucherValue: 0,
      pointsRemaining: POINTS_PER_VOUCHER,
      pct: 0,
      expiring: 0,
      provider: 'spotler',
      guid: ''
    };

    if (!data || typeof data !== 'object') return model;

    var status = data.status;
    /* Member gate: only "Active" data is trusted. Case-insensitive + trimmed —
       the Spotler source value may be "active" / "Active" (the audience rule
       matches `custom_lfmemberstatus contains active`). */
    if (unresolved(status) || String(status).trim().toLowerCase() !== 'active') return model;

    model.status = 'Active';
    model.guid = unresolved(data.guid) ? '' : String(data.guid);

    /* LOYALTY ENGAGE (UK since 2026-09-30). The Activate beacon sends whole POINTS (custom_le_points), not a euro
       balance, and vouchers are not issued automatically: the customer redeems 25 points for one voucher on the
       loyalty page. So "vouchers" here means vouchers the balance can buy, and the with-voucher state reads as
       "ready to redeem" (the UK copy says so). Spotler markets never set source, so nothing below changes for them. */
    if (data.source === 'le') {
      var lePoints = unresolved(data.points) ? 0 : (parseInt(String(data.points).replace(/[^0-9-]/g, ''), 10) || 0);
      if (lePoints < 0) lePoints = 0;
      model.provider = 'le';
      model.points = lePoints;
      model.vouchers = Math.floor(lePoints / POINTS_PER_VOUCHER);
      model.voucherValue = model.vouchers * VOUCHER_EUR;
      model.pointsRemaining = model.vouchers >= 1 ? 0 : POINTS_PER_VOUCHER - lePoints;
      model.pct = Math.min(Math.round((lePoints / POINTS_PER_VOUCHER) * 100), 100);
      model.expiring = unresolved(data.expiring) ? 0 : (parseInt(data.expiring, 10) || 0);
      model.state = model.vouchers >= 1 ? 'with-voucher' : 'no-voucher';
      return model;
    }

    /* Normalise European decimals ("2,50" -> 2.5) before parseFloat — the Spotler
       value can arrive comma-formatted and parseFloat("2,50") === 2 (the €2,00 bug). */
    var balance = data.balance;
    model.points = unresolved(balance)
      ? 0
      : Math.round((parseFloat(String(balance).replace(',', '.')) || 0) / EUR_PER_POINT);

    var vouchers = data.vouchers;
    model.vouchers = unresolved(vouchers) ? 0 : (parseInt(vouchers, 10) || 0);

    var vValue = data.voucherValue;
    /* Each active voucher is always €2.50, so the active total = count × €2.50.
       Derive it from the (authoritative) count rather than trusting the incoming
       value field, which may carry a per-voucher amount instead of the total.
       Fall back to the provided value only when there is no resolved count. */
    model.voucherValue = model.vouchers > 0
      ? (model.vouchers * VOUCHER_EUR)
      : (unresolved(vValue) ? 0 : (parseFloat(String(vValue).replace(',', '.')) || 0));

    model.pointsRemaining = Math.max(POINTS_PER_VOUCHER - model.points, 0);
    model.pct = Math.min(Math.round((model.points / POINTS_PER_VOUCHER) * 100), 100);

    model.state = model.vouchers >= 1 ? 'with-voucher' : 'no-voucher';
    return model;
  }

  /* ---- apply the model to the DOM contract ----------------------------- */

  var lastState = null;

  function apply(model) {
    var hints = readCurrencyHints();

    /* (d) set [data-lr-state] on every root */
    qsa('[data-lr-root]').forEach(function (root) {
      root.setAttribute('data-lr-state', model.state);
    });
    /* FAB roots also carry state so the launcher can re-tint. */
    qsa('[data-lr-fab]').forEach(function (fab) {
      fab.setAttribute('data-lr-state', model.state);
    });

    /* set html[data-lr-member] when state !== unknown; html[data-lr-voucher] only
       for the with-voucher state so the hub's email-claim form shows just then. */
    var html = document.documentElement;
    if (model.state !== 'unknown') html.setAttribute('data-lr-member', '');
    else html.removeAttribute('data-lr-member');
    if (model.state === 'with-voucher') html.setAttribute('data-lr-voucher', '');
    else html.removeAttribute('data-lr-voucher');
    /* html[data-lr-expiring] when Loyalty Engage reports points expiring within 30 days (UK only). */
    if (model.expiring > 0) html.setAttribute('data-lr-expiring', '');
    else html.removeAttribute('data-lr-expiring');

    /* fill every [data-lr-field] */
    qsa('[data-lr-field]').forEach(function (el) {
      var field = el.getAttribute('data-lr-field');
      switch (field) {
        case 'points':
          el.textContent = model.points;
          break;
        case 'vouchers':
          el.textContent = model.vouchers;
          break;
        case 'voucher-value':
          el.textContent = formatMoney(model.voucherValue, hints);
          break;
        case 'expiring':
          el.textContent = model.expiring || 0;
          break;
        case 'points-remaining':
          el.textContent = model.pointsRemaining;
          break;
        default:
          break;
      }
    });

    /* set width on every [data-lr-bar] to pct% */
    qsa('[data-lr-bar]').forEach(function (el) {
      el.style.width = model.pct + '%';
    });

    /* count-dependent phrases: any [data-lr-i18n="key"] gets t(key, {n}),
       where n = vouchers (the only count modules pluralise on). Modules may
       also call BWJLoyalty.t() directly for bespoke phrasing. */
    qsa('[data-lr-i18n]').forEach(function (el) {
      var key = el.getAttribute('data-lr-i18n');
      if (!key) return;
      el.textContent = t(key, { n: model.vouchers, points: model.points, remaining: model.pointsRemaining });
    });

    lastState = model.state;

    /* (f) dispatch applied event so modules can run post-paint hooks */
    try {
      document.dispatchEvent(new CustomEvent('bwj:loyalty:applied', { detail: model }));
    } catch (e) { /* CustomEvent unsupported — ignore */ }
  }

  /* Re-export the latest model for modules that load late. */
  function publish(model) {
    NS.model = model;
    apply(model);
  }

  /* ---- resolution lifecycle -------------------------------------------- */

  /* `gotMember` flips true once any source has yielded a real member state.
     It guards ONLY the timeout fallback, so a late beacon dispatch can never be
     ignored and the unknown default can never re-clobber real data. */
  var gotMember = false;

  function settle(data) {
    var model = computeModel(data);
    if (model.state !== 'unknown') gotMember = true;
    publish(model);
  }

  function resolve() {
    /* Render the default (unknown) immediately so content is never blank. */
    publish(computeModel(null));

    /* Persistent (NOT once) beacon listener: the beacon may create
       window.BWJLoyalty.data early with empty/placeholder fields and only
       populate the authoritative values + dispatch 'bwj:loyalty' LATER, and it
       may push again mid-session (e.g. a voucher minted as points cross 25).
       apply() is idempotent, so we re-resolve on every dispatch. */
    var onBeacon = function (e) {
      var d = (e && e.detail && typeof e.detail === 'object') ? e.detail : null;
      /* Same rule as pickData(): on a Loyalty Engage market a Spotler payload is ignored. */
      if (!d || (pageProvider() === 'le' && d.source !== 'le')) d = pickData();
      settle(d);
    };
    document.addEventListener('bwj:loyalty', onBeacon);

    /* Short-circuit the wait ONLY when the picked data already yields a member
       state; an early-but-empty .data object falls through so the listener +
       timeout can still settle once the real values arrive. */
    var data = pickData();
    var m = computeModel(data);
    if (data && m.state !== 'unknown') { settle(data); return; }

    window.setTimeout(function () {
      /* Fallback: if NO real member data has arrived, settle to whatever
         pickData() now yields (may still be null -> stays unknown). If a real
         member state already settled, leave the painted surfaces untouched. */
      if (gotMember) return;
      settle(pickData());
    }, SETTLE_TIMEOUT_MS);
  }

  /* ---- generic FAB behaviour ------------------------------------------- */
  /* Wires every [data-lr-fab] root. Toggle/close/backdrop are addressed by
     attribute so any number of FABs work without per-instance IDs.

       [data-lr-fab]            -> the fixed widget root
       [data-lr-fab-toggle]      -> launcher button
       [data-lr-fab-close]       -> any close button inside the panel
       [data-lr-fab-backdrop]    -> the dimming backdrop element
     Escape closes the open FAB. */

  function initFabs() {
    qsa('[data-lr-fab]').forEach(function (fab) {
      if (fab.__lrFabInit) return;
      fab.__lrFabInit = true;

      var backdrop = fab.querySelector('[data-lr-fab-backdrop]') ||
        document.querySelector('[data-lr-fab-backdrop][data-lr-fab-for="' + fab.id + '"]');
      var launcher = fab.querySelector('[data-lr-fab-toggle]');
      var panel = fab.querySelector('.lr-fab__panel');
      var closeBtn = fab.querySelector('[data-lr-fab-close]');

      function focusPanel() {
        /* Move focus into the dialog so keyboard/SR users land inside it and
           don't keep tabbing through the page behind the blocking backdrop.
           Prefer the close button; fall back to the panel (made focusable). */
        var target = closeBtn || panel;
        if (!target) return;
        if (target === panel && !panel.hasAttribute('tabindex')) {
          panel.setAttribute('tabindex', '-1');
        }
        try { target.focus(); } catch (e) { /* ignore */ }
      }

      function setOpen(open) {
        var wasOpen = fab.classList.contains('is-open');
        fab.classList.toggle('is-open', open);
        if (backdrop) backdrop.classList.toggle('is-visible', open);
        if (launcher) launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && !wasOpen) {
          focusPanel();
        } else if (!open && wasOpen && launcher) {
          /* Return focus to the launcher on close (click / backdrop / Escape). */
          try { launcher.focus(); } catch (e) { /* ignore */ }
        }
      }
      function open() { setOpen(true); }
      function close() { if (fab.classList.contains('is-open')) setOpen(false); }
      function toggle() { setOpen(!fab.classList.contains('is-open')); }

      on(launcher, 'click', toggle);
      qsa('[data-lr-fab-close]', fab).forEach(function (btn) { on(btn, 'click', close); });
      on(backdrop, 'click', close);

      /* expose per-fab control for modules that need it */
      fab.lrOpen = open;
      fab.lrClose = close;
      fab.lrToggle = toggle;
    });

    if (!NS.__escBound) {
      NS.__escBound = true;
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          qsa('[data-lr-fab].is-open').forEach(function (fab) {
            /* Reuse the per-fab close exposed by initFabs so the SIBLING
               backdrop (resolved via [data-lr-fab-for]) is hidden too — a
               bare fab.querySelector misses a backdrop placed outside the
               root and would leave the dimming overlay stuck on screen. */
            if (typeof fab.lrClose === 'function') {
              fab.lrClose();
            } else {
              fab.classList.remove('is-open');
              var l = fab.querySelector('[data-lr-fab-toggle]');
              if (l) l.setAttribute('aria-expanded', 'false');
            }
          });
        }
      });
    }
  }

  /* Allow modules injected after init to (re)wire & repaint. */
  NS.refresh = function () {
    initFabs();
    if (NS.model) apply(NS.model);
  };

  /* ---- boot ------------------------------------------------------------ */

  function boot() {
    initFabs();
    resolve();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
